import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminAuth, formatApiErrorDetail } from "@/lib/adminApi";
import NoIndex from "@/components/admin/NoIndex";
import { Loader2 } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    adminAuth
      .me()
      .then(() => {
        if (!cancelled) navigate("/admin/macd-connect", { replace: true });
      })
      .catch(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminAuth.login(email.trim(), password);
      navigate("/admin/macd-connect", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      // Localize known errors
      const map = {
        invalid_credentials: "E-mail ou senha inválidos.",
        admin_not_configured: "Área administrativa não configurada.",
      };
      let out = map[msg] || msg;
      if (msg.startsWith("too_many_attempts_wait_")) {
        const secs = parseInt(msg.replace("too_many_attempts_wait_", "").replace("s", ""), 10);
        out = `Muitas tentativas. Tente novamente em ${Math.ceil(secs / 60)} min.`;
      }
      setError(out);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white/60">
        <NoIndex />
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
      <NoIndex />
      <div className="w-full max-w-[420px]">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-block w-8 h-[3px] bg-[#f4c542]" />
            <span
              className="text-[11px] tracking-[0.28em] uppercase text-white/55"
              style={{ fontFamily: "Inter Tight, sans-serif" }}
            >
              Oasys Academy · Interno
            </span>
          </div>
          <h1
            className="text-4xl md:text-[2.6rem] leading-[1.05] tracking-tight"
            style={{ fontFamily: "Inter Tight, sans-serif", fontWeight: 700 }}
          >
            Área administrativa.
          </h1>
          <p
            className="mt-3 text-white/60 text-sm leading-relaxed"
            style={{ fontFamily: "Inter Tight, sans-serif" }}
          >
            Acesso restrito à equipe operacional da Academy.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-[11px] tracking-[0.24em] uppercase text-white/50 mb-2">
              E-mail
            </label>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#f4c542] outline-none py-3 text-white text-[15px] transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.24em] uppercase text-white/50 mb-2">
              Senha
            </label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#f4c542] outline-none py-3 text-white text-[15px] transition-colors"
            />
          </div>
          {error && (
            <p
              className="text-sm text-[#ff6b6b]"
              data-testid="admin-login-error"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full mt-4 bg-[#f4c542] hover:bg-[#e8b930] text-[#0a0a0a] font-medium py-3.5 px-6 tracking-tight text-sm uppercase transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ fontFamily: "Inter Tight, sans-serif", letterSpacing: "0.06em" }}
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            Entrar
          </button>
        </form>

        <p className="mt-12 text-[11px] tracking-[0.2em] uppercase text-white/30">
          Esta página não é indexada.
        </p>
      </div>
    </div>
  );
}
